import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from 'components/ui/Header';
import ContactQuickAccess from 'components/ui/ContactQuickAccess';
import Breadcrumb from 'components/ui/Breadcrumb';
import Icon from 'components/AppIcon';

import ProjectCard from './components/ProjectCard';
import ProjectLightbox from './components/ProjectLightbox';
import FilterControls from './components/FilterControls';

const ProjectGallery = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [filters, setFilters] = useState({
    serviceType: 'all',
    poolSize: 'all',
    location: 'all',
    sortBy: 'recent'
  });
  const [currentLanguage, setCurrentLanguage] = useState('es');

  const projects = [
    {
      id: 1,
      title: "Transformación Completa - Residencial Escazú",
      titleEn: "Complete Transformation - Escazú Residential",
      beforeImage: "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=800&h=600&fit=crop",
      afterImage: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800&h=600&fit=crop",
      serviceType: "deep-cleaning",
      poolSize: "large",
      location: "Escazú",
      completionDate: "2024-01-15",
      duration: "2 días",
      durationEn: "2 days",
      description: `Limpieza profunda de piscina residencial de lujo en Escazú. El proyecto incluyó remoción de algas, balanceado químico completo, limpieza de filtros y mantenimiento del sistema de circulación.

Desafíos superados: Agua extremadamente turbia debido a falta de mantenimiento por 6 meses. Nuestro equipo logró restaurar la claridad cristalina en tiempo récord.`,
      descriptionEn: `Deep cleaning of luxury residential pool in Escazú. Project included algae removal, complete chemical balancing, filter cleaning and circulation system maintenance.

Challenges overcome: Extremely cloudy water due to 6 months of neglect. Our team achieved crystal clear restoration in record time.`,
      rating: 5,
      customerName: "María González",
      challenges: ["Agua turbia severa", "Sistema de filtración obstruido", "Desequilibrio químico extremo"],
      challengesEn: ["Severe cloudy water", "Clogged filtration system", "Extreme chemical imbalance"]
    },
    {
      id: 2,
      title: "Mantenimiento Comercial - Hotel Presidente",
      titleEn: "Commercial Maintenance - Hotel Presidente",
      beforeImage: "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=800&h=600&fit=crop",
      afterImage: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&h=600&fit=crop",
      serviceType: "commercial",
      poolSize: "extra-large",
      location: "San José",
      completionDate: "2024-01-20",
      duration: "1 día",
      durationEn: "1 day",
      description: `Servicio de mantenimiento semanal para piscina comercial de hotel. Incluye limpieza de superficie, aspirado, balanceado químico y mantenimiento preventivo del equipo.

El hotel mantiene estándares de calidad excepcionales para sus huéspedes internacionales.`,
      descriptionEn: `Weekly maintenance service for commercial hotel pool. Includes surface cleaning, vacuuming, chemical balancing and preventive equipment maintenance.

The hotel maintains exceptional quality standards for its international guests.`,
      rating: 5,
      customerName: "Hotel Presidente",
      challenges: ["Alto volumen de uso", "Estándares comerciales estrictos", "Horarios de servicio limitados"],
      challengesEn: ["High usage volume", "Strict commercial standards", "Limited service hours"]
    },
    {
      id: 3,
      title: "Restauración de Piscina Abandonada - Cartago",
      titleEn: "Abandoned Pool Restoration - Cartago",
      beforeImage: "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=800&h=600&fit=crop",
      afterImage: "https://images.unsplash.com/photo-1566417713940-fe7c737a9ef2?w=800&h=600&fit=crop",
      serviceType: "restoration",
      poolSize: "medium",
      location: "Cartago",
      completionDate: "2024-01-10",
      duration: "3 días",
      durationEn: "3 days",
      description: `Proyecto de restauración completa de piscina abandonada por más de un año. Trabajo intensivo que incluyó drenaje completo, limpieza de paredes, reparación de sistema de filtración y llenado con tratamiento químico especializado.

Un verdadero desafío que demuestra nuestra experiencia en casos extremos.`,
      descriptionEn: `Complete restoration project of pool abandoned for over a year. Intensive work included complete drainage, wall cleaning, filtration system repair and refilling with specialized chemical treatment.

A true challenge that demonstrates our expertise in extreme cases.`,
      rating: 5,
      customerName: "Carlos Méndez",
      challenges: ["Abandono prolongado", "Daños estructurales menores", "Ecosistema de algas establecido"],
      challengesEn: ["Prolonged abandonment", "Minor structural damage", "Established algae ecosystem"]
    },
    {
      id: 4,
      title: "Piscina Residencial - Heredia",
      titleEn: "Residential Pool - Heredia",
      beforeImage: "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=800&h=600&fit=crop",
      afterImage: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&h=600&fit=crop",
      serviceType: "maintenance",
      poolSize: "small",
      location: "Heredia",
      completionDate: "2024-01-25",
      duration: "4 horas",
      durationEn: "4 hours",
      description: `Servicio de mantenimiento regular para piscina familiar en Heredia. Limpieza semanal que incluye aspirado, limpieza de paredes, balanceado químico y revisión de equipos.

Cliente satisfecho con nuestro servicio confiable y puntual.`,
      descriptionEn: `Regular maintenance service for family pool in Heredia. Weekly cleaning including vacuuming, wall cleaning, chemical balancing and equipment inspection.

Satisfied customer with our reliable and punctual service.`,
      rating: 5,
      customerName: "Ana Rodríguez",
      challenges: ["Uso familiar intensivo", "Mascotas en el área", "Mantenimiento preventivo"],
      challengesEn: ["Intensive family use", "Pets in the area", "Preventive maintenance"]
    },
    {
      id: 5,
      title: "Piscina de Lujo - Santa Ana",
      titleEn: "Luxury Pool - Santa Ana",
      beforeImage: "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=800&h=600&fit=crop",
      afterImage: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800&h=600&fit=crop",
      serviceType: "premium",
      poolSize: "large",
      location: "Santa Ana",
      completionDate: "2024-02-01",
      duration: "1 día",
      durationEn: "1 day",
      description: `Servicio premium para piscina de lujo en Santa Ana. Incluye limpieza detallada, tratamiento especializado del agua, limpieza de cascadas decorativas y mantenimiento de sistema de iluminación subacuática.

Atención personalizada para propiedades de alto valor.`,
      descriptionEn: `Premium service for luxury pool in Santa Ana. Includes detailed cleaning, specialized water treatment, decorative waterfall cleaning and underwater lighting system maintenance.

Personalized attention for high-value properties.`,
      rating: 5,
      customerName: "Roberto Silva",
      challenges: ["Elementos decorativos complejos", "Estándares de lujo", "Sistemas especializados"],
      challengesEn: ["Complex decorative elements", "Luxury standards", "Specialized systems"]
    },
    {
      id: 6,
      title: "Condominio Residencial - Alajuela",
      titleEn: "Residential Condominium - Alajuela",
      beforeImage: "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=800&h=600&fit=crop",
      afterImage: "https://images.unsplash.com/photo-1566417713940-fe7c737a9ef2?w=800&h=600&fit=crop",
      serviceType: "community",
      poolSize: "large",
      location: "Alajuela",
      completionDate: "2024-02-05",
      duration: "6 horas",
      durationEn: "6 hours",
      description: `Mantenimiento de piscina comunitaria en condominio residencial. Servicio que requiere coordinación con administración y cumplimiento de horarios específicos para no interrumpir las actividades de los residentes.

Servicio confiable para comunidades residenciales.`,
      descriptionEn: `Community pool maintenance in residential condominium. Service requiring coordination with management and compliance with specific schedules to not interrupt residents' activities.

Reliable service for residential communities.`,
      rating: 4,
      customerName: "Condominio Los Álamos",
      challenges: ["Coordinación comunitaria", "Horarios restringidos", "Alto volumen de usuarios"],
      challengesEn: ["Community coordination", "Restricted schedules", "High user volume"]
    }
  ];

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'es';
    setCurrentLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    let filtered = [...projects];

    // Apply filters
    if (filters.serviceType !== 'all') {
      filtered = filtered.filter(project => project.serviceType === filters.serviceType);
    }
    if (filters.poolSize !== 'all') {
      filtered = filtered.filter(project => project.poolSize === filters.poolSize);
    }
    if (filters.location !== 'all') {
      filtered = filtered.filter(project => project.location === filters.location);
    }

    // Apply sorting
    switch (filters.sortBy) {
      case 'recent':
        filtered.sort((a, b) => new Date(b.completionDate) - new Date(a.completionDate));
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.completionDate) - new Date(b.completionDate));
        break;
      case 'rating':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      default:
        break;
    }

    setFilteredProjects(filtered);
  }, [filters]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const openLightbox = (project) => {
    setSelectedProject(project);
  };

  const closeLightbox = () => {
    setSelectedProject(null);
  };

  const shareProject = (project) => {
    if (navigator.share) {
      navigator.share({
        title: currentLanguage === 'es' ? project.title : project.titleEn,
        text: currentLanguage === 'es' ? 'Mira esta transformación increíble de BAC Pool CR' : 'Check out this amazing transformation by BAC Pool CR',
        url: window.location.href
      });
    } else {
      // Fallback for browsers that don't support Web Share API
      const text = `${currentLanguage === 'es' ? project.title : project.titleEn} - BAC Pool CR`;
      navigator.clipboard.writeText(`${text} ${window.location.href}`);
      alert(currentLanguage === 'es' ? 'Enlace copiado al portapapeles' : 'Link copied to clipboard');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ContactQuickAccess />
      
      {/* Hero Section */}
      <section className="pt-20 pb-12 bg-gradient-to-br from-primary/10 to-secondary/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb />
          
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-primary mb-4">
              {currentLanguage === 'es' ? 'Galería de Proyectos' : 'Project Gallery'}
            </h1>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              {currentLanguage === 'es' ?'Descubre las transformaciones increíbles que hemos logrado. Cada proyecto cuenta una historia de excelencia y dedicación.' :'Discover the incredible transformations we have achieved. Each project tells a story of excellence and dedication.'
              }
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
            <div className="text-center">
              <div className="text-3xl font-heading font-bold text-primary mb-2">500+</div>
              <div className="text-sm text-text-secondary">
                {currentLanguage === 'es' ? 'Proyectos Completados' : 'Completed Projects'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-heading font-bold text-primary mb-2">98%</div>
              <div className="text-sm text-text-secondary">
                {currentLanguage === 'es' ? 'Satisfacción Cliente' : 'Customer Satisfaction'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-heading font-bold text-primary mb-2">5</div>
              <div className="text-sm text-text-secondary">
                {currentLanguage === 'es' ? 'Años Experiencia' : 'Years Experience'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-heading font-bold text-primary mb-2">24/7</div>
              <div className="text-sm text-text-secondary">
                {currentLanguage === 'es' ? 'Soporte Emergencia' : 'Emergency Support'}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Controls */}
      <section className="py-8 bg-surface border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FilterControls
            filters={filters}
            onFilterChange={handleFilterChange}
            currentLanguage={currentLanguage}
          />
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredProjects.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="Search" size={48} className="text-text-secondary mx-auto mb-4" />
              <h3 className="text-xl font-heading font-semibold text-text-primary mb-2">
                {currentLanguage === 'es' ? 'No se encontraron proyectos' : 'No projects found'}
              </h3>
              <p className="text-text-secondary">
                {currentLanguage === 'es' ?'Intenta ajustar los filtros para ver más resultados' :'Try adjusting the filters to see more results'
                }
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  currentLanguage={currentLanguage}
                  onViewDetails={openLightbox}
                  onShare={shareProject}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-primary to-secondary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
            {currentLanguage === 'es' ?'¿Listo para Transformar tu Piscina?' :'Ready to Transform Your Pool?'
            }
          </h2>
          <p className="text-xl text-white/90 mb-8">
            {currentLanguage === 'es' ?'Únete a cientos de clientes satisfechos. Solicita tu cotización gratuita hoy.' :'Join hundreds of satisfied customers. Request your free quote today.'
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact-quote-request"
              className="inline-flex items-center justify-center space-x-2 bg-white text-primary px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 nav-transition"
            >
              <Icon name="MessageSquare" size={20} />
              <span>
                {currentLanguage === 'es' ? 'Solicitar Cotización' : 'Request Quote'}
              </span>
            </Link>
            <a
              href="tel:+50688888888"
              className="inline-flex items-center justify-center space-x-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-primary nav-transition"
            >
              <Icon name="Phone" size={20} />
              <span>+506 8888-8888</span>
            </a>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {selectedProject && (
        <ProjectLightbox
          project={selectedProject}
          currentLanguage={currentLanguage}
          onClose={closeLightbox}
          onShare={shareProject}
        />
      )}

      {/* Footer */}
      <footer className="bg-text-primary text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="Waves" size={24} color="white" />
                </div>
                <div>
                  <h3 className="text-xl font-heading font-bold">BAC Pool CR</h3>
                  <p className="text-sm text-gray-300">Professional Pool Cleaning Services</p>
                </div>
              </div>
              <p className="text-gray-300 mb-4">
                {currentLanguage === 'es' ?'Expertos en limpieza y mantenimiento de piscinas en Costa Rica. Servicio profesional, confiable y de calidad garantizada.' :'Pool cleaning and maintenance experts in Costa Rica. Professional, reliable service with guaranteed quality.'
                }
              </p>
            </div>
            
            <div>
              <h4 className="font-heading font-semibold mb-4">
                {currentLanguage === 'es' ? 'Contacto' : 'Contact'}
              </h4>
              <div className="space-y-2 text-gray-300">
                <div className="flex items-center space-x-2">
                  <Icon name="Phone" size={16} />
                  <span>+506 8888-8888</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="MessageCircle" size={16} />
                  <span>WhatsApp</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} />
                  <span>Costa Rica</span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-heading font-semibold mb-4">
                {currentLanguage === 'es' ? 'Horarios' : 'Schedule'}
              </h4>
              <div className="space-y-2 text-gray-300">
                <div>{currentLanguage === 'es' ? 'Lun-Sáb: 7AM-6PM' : 'Mon-Sat: 7AM-6PM'}</div>
                <div>{currentLanguage === 'es' ? 'Dom: Emergencias' : 'Sun: Emergencies'}</div>
                <div className="text-accent font-medium">
                  {currentLanguage === 'es' ? 'Servicio 24/7' : '24/7 Service'}
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-300">
            <p>&copy; {new Date().getFullYear()} BAC Pool CR. {currentLanguage === 'es' ? 'Todos los derechos reservados.' : 'All rights reserved.'}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProjectGallery;